import {SessionState} from '@github-ui/agent-sessions/types/session'
import {OPEN_DELAY} from '@github-ui/hovercards/constants'
import {testIdProps} from '@github-ui/test-id-props'
import {KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, AnchoredOverlay, IconButton, RelativeTime} from '@primer/react'
import {clsx} from 'clsx'
import {type FocusEvent, memo, useCallback, useRef, useState} from 'react'

import {useRemoveAgentAssignment} from '../../hooks/use-remove-agent-assignment'
import styles from './agent-task-popover.module.css'
import kebabStyles from './kebab-menu.module.css'

// Delay before showing the popover to prevent rapid tooltip flashing when hovering across rows
const POPOVER_SHOW_DELAY_MS = OPEN_DELAY
// Delay before hiding the popover to allow mouse movement from anchor to overlay content
const POPOVER_HIDE_DELAY_MS = 200

export interface AgentTaskPopoverTaskItem {
  id?: string
  title?: string
  nwo?: string
  state?: SessionState
  createdAt?: string
  lastUpdatedAt?: string
  href?: string
  assignmentId?: number
  sourceRepoNwo?: string
}

interface AgentTaskPopoverProps {
  /** Array of tasks to display in the popover. */
  tasks: Array<AgentTaskPopoverTaskItem>
  children: React.ReactNode
  /** Optional className for the popover anchor wrapper div */
  className?: string
  /** When provided, clicking a task item calls this instead of navigating. */
  onTaskClick?: (taskId: string, e: React.MouseEvent) => void
}

function getTaskKey(task: AgentTaskPopoverTaskItem, fallbackIndex: number): string | number {
  return task.assignmentId ?? task.id ?? task.href ?? `${task.title ?? 'task'}-${task.createdAt ?? fallbackIndex}`
}

function TaskItemContent({
  title,
  state,
  lastUpdatedAt,
}: {
  title?: string
  state?: SessionState
  lastUpdatedAt?: string
}) {
  const isQueued = state === SessionState.Queued
  const taskLastUpdatedAt = lastUpdatedAt

  return (
    <>
      {!isQueued && taskLastUpdatedAt && (
        <div className={styles.elapsedTime}>
          Updated <RelativeTime date={new Date(taskLastUpdatedAt)} />
        </div>
      )}
      {title && <div className={styles.title}>{title}</div>}
    </>
  )
}

export function RemoveSessionMenu({
  assignmentId,
  sourceRepoNwo,
  onRemoveAssignment,
  className,
}: {
  assignmentId: number
  sourceRepoNwo: string
  onRemoveAssignment?: (assignmentId: number) => void
  className?: string
}) {
  const {removeAssignment, isRemoving} = useRemoveAgentAssignment({
    assignmentId,
    sourceRepoNwo,
    onRemoveAssignment,
  })

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton
          icon={KebabHorizontalIcon}
          variant="invisible"
          size="small"
          aria-label="Session options"
          className={className ? `${kebabStyles.kebabButton} ${className}` : kebabStyles.kebabButton}
          onClick={e => e.stopPropagation()}
        />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="medium">
        <ActionList>
          <ActionList.Item onSelect={removeAssignment} disabled={isRemoving}>
            Remove session from issue
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function TaskListItem({
  task,
  onRemoveAssignment,
  onTaskClick,
}: {
  task: AgentTaskPopoverTaskItem
  onRemoveAssignment?: (assignmentId: number) => void
  onTaskClick?: (taskId: string, e: React.MouseEvent) => void
}) {
  const content = <TaskItemContent title={task.title} state={task.state} lastUpdatedAt={task.lastUpdatedAt} />
  const {assignmentId, sourceRepoNwo} = task
  const canRemove = assignmentId != null && sourceRepoNwo != null

  return (
    <div className={styles.taskItemRow}>
      {task.href ? (
        <a
          href={task.href}
          target="_blank"
          rel="noreferrer"
          className={styles.taskItem}
          onClick={
            onTaskClick && task.id
              ? (e: React.MouseEvent) => {
                  const id = task.id as string
                  onTaskClick(id, e)
                }
              : undefined
          }
        >
          {content}
        </a>
      ) : (
        <div className={styles.taskItem}>{content}</div>
      )}
      {canRemove && (
        <RemoveSessionMenu
          assignmentId={assignmentId}
          sourceRepoNwo={sourceRepoNwo}
          onRemoveAssignment={onRemoveAssignment}
          className={styles.kebabButton}
        />
      )}
    </div>
  )
}

function TaskListContent({
  tasks,
  showPopover,
  hidePopover,
  caretPosition,
  onTaskClick,
}: {
  tasks: Array<AgentTaskPopoverTaskItem>
  showPopover: () => void
  hidePopover: () => void
  caretPosition: 'bottomLeft' | 'bottomRight'
  onTaskClick?: (taskId: string, e: React.MouseEvent) => void
}) {
  const [removedIds, setRemovedIds] = useState<Set<number>>(() => new Set())
  const listRef = useRef<HTMLDivElement>(null)

  const handleRemove = useCallback(
    (assignmentId: number) => {
      setRemovedIds(prev => {
        const next = new Set(prev).add(assignmentId)
        // Determine how many tasks will remain after this removal
        const remaining = tasks.filter(t => t.assignmentId == null || !next.has(t.assignmentId))
        if (remaining.length === 0) {
          // No tasks left — close the popover which returns focus to anchor
          hidePopover()
        } else {
          // Move focus to the next visible task row
          requestAnimationFrame(() => {
            const container = listRef.current
            if (!container) return
            const nextFocusable = container.querySelector<HTMLElement>(
              '[class*="taskItemRow"] a, [class*="taskItemRow"] [role="button"]',
            )
            nextFocusable?.focus()
          })
        }
        return next
      })
    },
    [hidePopover, tasks],
  )

  const handleBlur = useCallback(
    (event: FocusEvent<HTMLDivElement>) => {
      const relatedTarget = event.relatedTarget as HTMLElement | null
      if (!relatedTarget || !event.currentTarget.contains(relatedTarget)) {
        hidePopover()
      }
    },
    [hidePopover],
  )

  const visibleTasks = tasks.filter(t => t.assignmentId == null || !removedIds.has(t.assignmentId))

  return (
    <div
      className={styles.popoverContainer}
      onMouseEnter={showPopover}
      onMouseLeave={hidePopover}
      onFocus={showPopover}
      onBlur={handleBlur}
      {...testIdProps('agent-task-popover')}
    >
      <div
        ref={listRef}
        className={clsx(styles.taskList, caretPosition === 'bottomRight' ? styles.bottomRight : styles.bottomLeft)}
      >
        {visibleTasks.map((task, index) => (
          <TaskListItem
            key={getTaskKey(task, index)}
            task={task}
            onRemoveAssignment={handleRemove}
            onTaskClick={onTaskClick}
          />
        ))}
      </div>
    </div>
  )
}

export const AgentTaskPopover = memo(function AgentTaskPopover({
  tasks,
  children,
  className,
  onTaskClick,
}: AgentTaskPopoverProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [caretPosition, setCaretPosition] = useState<'bottomLeft' | 'bottomRight'>('bottomLeft')
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)

  const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showPopover = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    if (showTimeoutRef.current) return
    showTimeoutRef.current = setTimeout(() => {
      showTimeoutRef.current = null
      setIsOpen(true)
    }, POPOVER_SHOW_DELAY_MS)
  }, [])

  const showPopoverImmediate = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current)
      showTimeoutRef.current = null
    }
    setIsOpen(true)
  }, [])

  const hidePopover = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current)
      showTimeoutRef.current = null
    }
    hideTimeoutRef.current = setTimeout(() => {
      setIsOpen(false)
    }, POPOVER_HIDE_DELAY_MS)
  }, [])

  const handleClose = useCallback(() => {
    setIsOpen(false)
  }, [])

  const handlePositionChange = useCallback(({position}: {position: {anchorAlign: string}}) => {
    setCaretPosition(position.anchorAlign === 'end' ? 'bottomRight' : 'bottomLeft')
  }, [])

  if (!tasks || tasks.length === 0) {
    return <>{children}</>
  }

  return (
    <>
      <div
        ref={anchorRef}
        className={clsx(styles.popoverWrapper, className)}
        onMouseEnter={showPopover}
        onMouseLeave={hidePopover}
        onFocusCapture={showPopoverImmediate}
        onBlurCapture={hidePopover}
        {...testIdProps('agent-task-popover-wrapper')}
      >
        {children}
      </div>
      <AnchoredOverlay
        open={isOpen}
        renderAnchor={null}
        anchorRef={anchorRef}
        onClose={handleClose}
        onPositionChange={handlePositionChange}
        focusTrapSettings={{disabled: true}}
        side="outside-top"
        align="start"
        overlayProps={{
          preventFocusOnOpen: true,
          role: 'dialog',
          'aria-label': 'Agent tasks',
          overflow: 'visible',
          className: styles.overlay,
        }}
      >
        <TaskListContent
          tasks={tasks}
          showPopover={showPopover}
          hidePopover={hidePopover}
          caretPosition={caretPosition}
          onTaskClick={onTaskClick}
        />
      </AnchoredOverlay>
    </>
  )
})
